import { Maybe, Org, User, CreateIntercomTokenDocument } from "src/types.g";
import Intercom, {
  hide,
  startTour,
  trackEvent,
  shutdown,
  onShow,
} from "@intercom/messenger-js-sdk";
import { appConfig } from "src/appConfig";
import { Subscription } from "src/billinglatest/types";
import { toUnixTimestamp } from "./date";
import { InitType } from "@intercom/messenger-js-sdk/dist/types";
import apolloClient from "src/state/apolloClient";
import { datadogLogs } from "@datadog/browser-logs";

export enum IntercomEvent {
  SCREEN_PAIRING_ONBOARDING_COMPLETE = "studio_screen_pairing_onboarding_complete",
  DESIGN_SCREEN_ONBOARDING_COMPLETE = "studio_design_screen_onboarding_complete",
  SET_FIRST_CONTENT_TO_SCREEN_COMPLETE = "studio_set_first_content_to_screen_complete",
}

let initailIntercomParams: InitType | null = null;

/**
 * Fetch JWT token from the server for Intercom authentication.
 */
const fetchIntercomJwtToken = async (): Promise<string | null> => {
  try {
    const result = await apolloClient.mutate({
      mutation: CreateIntercomTokenDocument,
    });
    const token = result.data?.createIntercomToken?.token ?? null;

    if (!token) {
      const error = new Error(
        "Failed to fetch Intercom JWT token: token is null",
      );
      console.error(error.message);
      datadogLogs.logger.error(error.message, {
        error: {
          message: error.message,
          stack: error.stack,
          name: error.name,
        },
        context: "fetchIntercomJwtToken",
      });
      return null;
    }

    return token;
  } catch (error) {
    const err =
      error instanceof Error
        ? error
        : new Error(`Failed to fetch Intercom JWT token: ${String(error)}`);
    console.error(err.message);
    datadogLogs.logger.error(err.message, {
      error: {
        message: err.message,
        stack: err.stack,
        name: err.name,
      },
      context: "fetchIntercomJwtToken",
    });
    return null;
  }
};

export const initIntercom = async ({
  user,
  org,
  subscription,
}: {
  user: Maybe<User>;
  org: Maybe<Org>;
  subscription: Maybe<Subscription>;
}) => {
  if (!user || !org || !subscription) {
    return;
  }

  const appId = appConfig.intercomAppId;
  const name = `${user.givenName} ${user.familyName}`;
  const createdAt = user.createdAt;
  const orgId = org?.id;
  const orgName = org?.name;

  // Fetch JWT token for Intercom authentication
  const intercomUserJwt = await fetchIntercomJwtToken();

  if (!intercomUserJwt) {
    return; // Don't initialize - error already logged in fetchIntercomJwtToken
  }

  initailIntercomParams = {
    app_id: appId,
    name,
    created_at: createdAt,
    ...(intercomUserJwt && { intercom_user_jwt: intercomUserJwt }),
    company: {
      id: orgId,
      name: orgName,
      status: subscription.status,
      created_at: org?.createdAt ? toUnixTimestamp(org.createdAt) : undefined,
    },
  };

  Intercom(initailIntercomParams);
};

const closeIntercom = () => {
  hide();
};

export const restartIntercom = async () => {
  if (initailIntercomParams) {
    shutdown();

    // Fetch new JWT token for Intercom authentication
    const intercomUserJwt = await fetchIntercomJwtToken();

    if (!intercomUserJwt) {
      return; // Don't restart - error already logged in fetchIntercomJwtToken
    }

    // Update params with fresh token
    const updatedParams = {
      ...initailIntercomParams,
      ...(intercomUserJwt && { intercom_user_jwt: intercomUserJwt }),
    };

    Intercom(updatedParams);
    initailIntercomParams = updatedParams;
  }
};

export const startSetScreenContentTour = () => {
  startTour(appConfig.intercomTours.setScreenContentTourId);
};

const disconnectObserver = (
  observer: MutationObserver,
  frameDocument: Document,
) => {
  const checkListRemainingTime = frameDocument.querySelector(
    '[data-testid="checklist-remaining-time"]',
  );
  if (checkListRemainingTime?.innerHTML.includes("Completed")) {
    observer.disconnect();
  }
};

export const trackIntercomEvent = async (eventName: IntercomEvent) => {
  try {
    await trackEvent(eventName);
  } catch (error) {
    console.error("Failed to track Intercom event:", error);
  }
};

export const watchIntercomButton = ({
  buttonName,
  callback,
}: {
  buttonName: string;
  callback: () => void;
}) => {
  const watchTaskList = (frameDocument: Document) => {
    // Create observer for task list changes
    const taskListObserver = new MutationObserver((mutations) => {
      const taskList = frameDocument?.querySelector(
        '[data-testid="checklist-task-list"]',
      );

      if (taskList) {
        // Create a separate observer for the task list content
        const taskListContentObserver = new MutationObserver((mutations) => {
          // Look for "buttonName" buttons by aria-label
          const button = frameDocument?.querySelector(
            `[aria-label="${buttonName}"]`,
          );

          if (button && !button.hasAttribute("data-custom-handler")) {
            // Mark the button as processed
            button.setAttribute("data-custom-handler", "true");

            // Add click handler
            button.addEventListener(
              "click",
              (e) => {
                e.preventDefault();
                e.stopPropagation();
                callback();
                closeIntercom();
              },
              true,
            );
          }
        });

        // Observe the task list content for changes
        taskListContentObserver.observe(taskList, {
          childList: true,
          subtree: true,
          attributes: true,
        });
      }
    });

    // Observe the entire frame document for task list appearance
    taskListObserver.observe(frameDocument, {
      childList: true,
      subtree: true,
    });
  };

  const observer = new MutationObserver((mutations, obs) => {
    const intercomFrame = document.querySelector("#intercom-container iframe");
    if (intercomFrame) {
      const frameDocument = (intercomFrame as HTMLIFrameElement)
        .contentDocument;
      if (frameDocument) {
        watchTaskList(frameDocument);
        disconnectObserver(obs, frameDocument);
      }
    }
  });

  // Main observer for the iframe
  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
};

export const onIntercomShow = (callback: () => void) => {
  onShow(() => {
    callback();
  });
};
